import React from 'react';
import type {
  ImageProps,
  ImageResizeMode,
  ImageSourcePropType,
} from 'react-native';
import { Image as RNImage, Platform } from 'react-native';
import { Image } from './Image';
import { CloudinaryImage } from './CloudinaryImage';

// Loads remote images through cloudinary and falls back to local image.
export const FallbackImage = React.memo(
  ({
    baseUrl,
    testId,
    defaultImage,
    resizeMode,
    style,
    cloudinaryConfig,
  }: Props) => {
    // Image Error
    const [imageLoadError, setImageLoadError] = React.useState(false);
    const handleImageLoadError = React.useCallback(() => {
      setImageLoadError(true);
    }, [setImageLoadError]);

    // use cloudinary image component for remote images
    if (baseUrl && !imageLoadError) {
      return (
        <CloudinaryImage
          style={style}
          resizeMode={resizeMode}
          onError={handleImageLoadError}
          baseUrl={baseUrl}
          config={cloudinaryConfig}
          testID={`${testId}-remote`}
        />
      );
    }

    // ...and default image component for local images and when remote image can't be fetched
    return (
      <Image
        style={style}
        resizeMode={resizeMode}
        source={defaultImage}
        accessibilityIgnoresInvertColors
        testID={`${testId}-local`}
      />
    );
  },
);

if (Platform.OS === 'web') {
  // eslint-disable-next-line functional/immutable-data
  RNImage.resolveAssetSource = (source) => {
    return {
      uri: String(source),
      width: 1,
      height: 1,
      scale: 1,
    };
  };
}

type Props = {
  readonly testId: string;
  readonly baseUrl: string;
  readonly defaultImage: ImageSourcePropType;
  readonly resizeMode: ImageResizeMode;
  readonly cloudinaryConfig: React.ComponentProps<
    typeof CloudinaryImage
  >['config'];
} & Partial<ImageProps>;
